import React, {
	createContext,
	type ReactNode,
	useContext,
	useEffect,
	useReducer,
	useRef,
} from 'react';

import __noop from '@atlaskit/ds-lib/noop';
import { setBooleanFeatureFlagResolver } from '@atlaskit/platform-feature-flags';

import { useFeatureFlagOverrides } from './feature-flag-overrides';

export type FeatureFlagList = {
	'platform-component-visual-refresh': boolean;
	'platform-visual-refresh-icons': boolean;
};

/**
 * Feature flag defaults
 */
export const defaultFeatureFlags: FeatureFlagList = {
	'platform-component-visual-refresh': true,
	'platform-visual-refresh-icons': true,
};

type FeatureFlagContextProps = {
	features: FeatureFlagList;
	setFeatureFlag: (featureFlagName: string, value: boolean) => void;
};

const FeatureFlagContext = createContext<FeatureFlagContextProps>({
	features: defaultFeatureFlags,
	setFeatureFlag: __noop,
});

export const useFeatureFlags = () => {
	const { features, setFeatureFlag } = useContext(FeatureFlagContext);

	return { features, setFeatureFlag };
};

type Action = { type: 'SET_FEATURE_FLAG'; payload: { featureFlagName: string; value: boolean } };

const featureFlagReducer = (state: FeatureFlagList, action: Action): FeatureFlagList => {
	switch (action.type) {
		case 'SET_FEATURE_FLAG':
			const { featureFlagName, value } = action.payload;
			return {
				...state,
				[featureFlagName]: value,
			};
		default:
			return state;
	}
};

type FeatureFlagProviderProps = {
	children?: ReactNode;
	/**
	 * Initial feature flags and values to use.
	 * If not provided, the default feature flags will be used.
	 */
	initialFeatureFlags?: FeatureFlagList;
};

/**
 * __Feature flag provider__
 *
 * Provides feature flag configuration to components within.
 * Allows feature flags to be updated, or overridden for limited subtrees
 * in combination with a `FeatureFlagOverride`.
 */
export const FeatureFlagProvider = ({
	children,
	initialFeatureFlags,
}: FeatureFlagProviderProps) => {
	const [features, dispatch] = useReducer(
		featureFlagReducer,
		initialFeatureFlags ?? defaultFeatureFlags,
	);

	const isResolverSet = useRef(false);

	if (isResolverSet.current === false) {
		setBooleanFeatureFlagResolver(useResolver);
		isResolverSet.current = true;
	}

	useEffect(() => {
		return () => {
			setBooleanFeatureFlagResolver(() => false);
		};
	}, []);

	const setFeatureFlag = (featureFlagName: string, value: boolean) => {
		dispatch({ type: 'SET_FEATURE_FLAG', payload: { featureFlagName, value } });
	};

	return (
		<FeatureFlagContext.Provider
			value={{
				features,
				setFeatureFlag,
			}}
		>
			{children}
		</FeatureFlagContext.Provider>
	);
};

const useResolver = (featureFlagName: string) => {
	const key = featureFlagName as keyof FeatureFlagList;
	const { features } = useFeatureFlags();
	const overrides = useFeatureFlagOverrides();

	if (Object.keys(overrides).length > 0 && overrides[key] !== undefined) {
		return overrides[key] || false;
	}

	return features[key] || false;
};
